import moment from 'moment-timezone';
import { WEEK_DAYS, WEEK_DAY_NAMES } from '../../../constants/forecast';

// source_status values computed by BE (ALT-2230)
export const FORECAST_SOURCE_STATUS = {
  UPDATED: 'UPDATED',
  WAITING: 'WAITING',
  ROUTING_NOT_SELECTED: 'ROUTING_NOT_SELECTED',
};

const SOURCE_DATE_FORMAT = 'MM/DD';
const CELL_SOURCE_DATE_FORMAT = 'MM/DD/YYYY';
const NO_FORECAST_SOURCE_TEXT = 'No forecast source available.';
const MANUAL_FORECAST_TEXT = 'Manual forecast, not calculated from routes.';
const formatDate = (date, timezone, format = SOURCE_DATE_FORMAT) => {
  return moment.tz(date, timezone || moment.tz.guess()).format(format);
};

// Date of `weekDay` in the current week (Monday → Sunday), in the region's timezone (local timezone if none)
const getThisWeekDate = (weekDay, timezone) => {
  const now = timezone ? moment.tz(timezone) : moment();
  return now.isoWeekday(WEEK_DAYS.indexOf(weekDay) + 1);
};
  
const getPrefix = (source, now) => {
  if (source.isSame(now.clone().subtract(1, 'week'), 'isoWeek')) return 'last';
  if (source.isSame(now, 'isoWeek')) return 'this';
  if (source.isSame(now.clone().add(1, 'week'), 'isoWeek')) return 'next';
  return null;
};

// "last / this / next Tuesday's (10/06)" or "the Tuesday of 09/29"
const formatSourceDay = ({ dayName, sourceDate, timezone }) => {
  const source = moment.tz(sourceDate, timezone || moment.tz.guess());
  const sourceDateText = source.format(SOURCE_DATE_FORMAT);
  const now = timezone ? moment.tz(timezone) : moment();
  const prefix = getPrefix(source, now);
  
  return prefix ? `${prefix} ${dayName}'s (${sourceDateText})` : `the ${dayName} of ${sourceDateText}`;
};

/**
 * Builds the day column header tooltip that says which routed day the forecast is based on.
 * - weekDay: 'MON' … 'SUN'
 * - isAutoMode: whether the day's forecast is auto-calculated (AUTO off → values are hand-set)
 * - percentage: the day's auto forecast % (number | null)
 * - sourceDate / sourceStatus: forecast-level `source_date` / `source_status` (nullable)
 * - timezone: the region's IANA timezone (nullable)
 */
export const getForecastSourceTooltip = ({ weekDay, isAutoMode, percentage, sourceDate, sourceStatus, timezone }) => {
  if (!isAutoMode) {
    return MANUAL_FORECAST_TEXT;
  }

  const dayName = WEEK_DAY_NAMES[weekDay] || weekDay;
  const percentageText = [null, undefined].includes(percentage) ? `${weekDay} %` : `${percentage}%`;
  const sourceDateText = sourceDate ? formatDate(sourceDate, timezone) : null;

  if (!sourceDateText) {
    return NO_FORECAST_SOURCE_TEXT;
  }

  const sourceDayText = formatSourceDay({ dayName, sourceDate, timezone });

  switch (sourceStatus) {
    case FORECAST_SOURCE_STATUS.UPDATED:
      return `Based on ${sourceDayText} selected routes × ${percentageText}. Updated when routing was selected.`;
    case FORECAST_SOURCE_STATUS.WAITING:
      return `Based on ${sourceDayText} actual routes × ${percentageText}. This ${dayName}'s routes will be used once routing is selected.`;
    case FORECAST_SOURCE_STATUS.ROUTING_NOT_SELECTED: {
      const thisWeekDateText = getThisWeekDate(weekDay, timezone).format(SOURCE_DATE_FORMAT);
      return `Routing wasn't selected for this ${dayName} (${thisWeekDateText}), so this forecast still uses ${sourceDayText} actual routes.`;
    }
    default:
      return NO_FORECAST_SOURCE_TEXT;
  }
};

// Tooltip for a cell's system forecast value - isAutoMode: the cell's day column AUTO state, sourceDate: zone-level `source_date` (nullable)
export const getCellSourceTooltip = ({ isAutoMode, sourceDate, timezone }) => {
  if (!isAutoMode) {
    return MANUAL_FORECAST_TEXT;
  }

  return sourceDate ? `Value calculated from ${formatDate(sourceDate, timezone, CELL_SOURCE_DATE_FORMAT)}` : NO_FORECAST_SOURCE_TEXT;
};
